// useMembershipRecheckOnResume.ts — for the membership surfaces (paywall, unlock walls,
// trial-ended sheet). While `active`: re-read entitlement the moment the app returns to the
// foreground (someone who paid on another device and comes back), and call `onMember` when
// membership flips on. auth.recheck() is not subject to useAuth's once-a-day resume cap.
import React from 'react';
import { App as CapApp } from '@capacitor/app';
import { useAuthOptional } from '@/hooks/useAuth';

/** One resume can fire both the native appStateChange and a visibilitychange. */
export const RESUME_DEBOUNCE_MS = 1500;

export function useMembershipRecheckOnResume(active: boolean, onMember?: () => void): void {
  const auth = useAuthOptional();
  const recheckRef = React.useRef(auth?.recheck);
  recheckRef.current = auth?.recheck;
  const onMemberRef = React.useRef(onMember);
  onMemberRef.current = onMember;

  const isMember = !!auth?.isMember;
  const prevMember = React.useRef(isMember);
  React.useEffect(() => {
    const was = prevMember.current;
    prevMember.current = isMember;
    if (active && !was && isMember) onMemberRef.current?.();
  }, [active, isMember]);

  React.useEffect(() => {
    if (!active) return;
    let last = 0;
    const fire = () => {
      const now = Date.now();
      if (now - last < RESUME_DEBOUNCE_MS) return;
      last = now;
      recheckRef.current?.().catch(() => undefined);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') fire(); };
    document.addEventListener('visibilitychange', onVisible);
    const handle = CapApp.addListener('appStateChange', ({ isActive }: { isActive: boolean }) => { if (isActive) fire(); });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      handle.then((h) => h.remove()).catch(() => undefined);
    };
  }, [active]);
}
